/**
 * Intercepteur global d'authentification.
 *
 * Le serveur signale tout refus d'authentification par un 401 dont le corps
 * porte { error, code, deconnexion: true }. Quel que soit l'écran et quel que
 * soit l'appel (les composants utilisent fetch directement), la réaction est
 * la même : purger la session locale, mémoriser le motif pour la page de
 * connexion, et recharger — l'application affiche alors l'écran de connexion.
 *
 * Deux garde-fous :
 *  - un 401 SANS session locale (échec de connexion : mauvais mot de passe,
 *    utilisateur inconnu) est laissé au formulaire de connexion, qui sait
 *    l'afficher — recharger effacerait son message ;
 *  - après le premier 401 traité, l'intercepteur ne rend jamais la main
 *    (promesse en attente pendant le rechargement) : aucun composant ne peut
 *    afficher ce 401 en notification, et les 401 concurrents des autres
 *    appels en cours sont absorbés de la même façon.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const CLE_MOTIF = 'session_expiree_motif';
// Le DÉTAIL de la fermeture (quel appel, quel code, quelle raison), pour la
// page de connexion. Vécu 27.09 : « à chaque fois je suis déconnecté » — et
// pour savoir POURQUOI, il fallait aller lire le journal du serveur. La
// personne qui vient d'être éjectée a la réponse sous les yeux si on la lui
// écrit ; sans ça elle nous envoie la mauvaise requête de l'onglet réseau.
const CLE_DETAIL = 'session_expiree_detail';
const ENTETE_JETON = 'X-Session-Token';
let dejaTraite = false;

/**
 * UN 401 N'EST PAS TOUJOURS UNE FIN DE SESSION.
 *
 * Le serveur normalise ses refus d'authentification : `deconnexion: true` et
 * un `code` (auth_requise, token_invalide, token_expire, utilisateur_inconnu,
 * account_disabled). Tout autre 401 vient d'ailleurs — d'une route qui vérifie
 * un secret de webhook, d'un relais, d'un service tiers. Déconnecter sur
 * celui-là, c'est éjecter quelqu'un dont la session est parfaitement valable,
 * et lui faire chercher un problème de compte qui n'existe pas.
 *
 * La règle vit ICI, en un seul endroit : l'intercepteur global la suivait
 * déjà, `APIService.handleResponse` avait la sienne (« 401 + un jeton en
 * mémoire = dehors »), et une purge sans message ne laissait RIEN à lire.
 */
const CODES_FIN_DE_SESSION = new Set([
  'auth_requise', 'token_invalide', 'token_expire', 'utilisateur_inconnu', 'account_disabled',
]);
export function estRefusAuth(corps) {
  if (!corps || typeof corps !== 'object') return false;
  return corps.deconnexion === true || CODES_FIN_DE_SESSION.has(corps.code);
}

/**
 * Fermer la session : le motif d'abord (la page de connexion le lit), la
 * purge ensuite, le rechargement en dernier. Idempotent — plusieurs appels
 * concurrents ne rechargent qu'une fois.
 *
 * `ou` (l'appel qui a été refusé) ne va PAS à l'écran : personne n'a à lire
 * une adresse d'API. Il part au journal du navigateur, là où on diagnostique.
 */
export function fermerSession(motif, ou = null, corps = null) {
  if (dejaTraite) return;
  dejaTraite = true;
  const code = corps && corps.code ? String(corps.code) : null;
  const raison = corps && corps.raison ? String(corps.raison) : null;
  console.warn(`🔒 Session fermée sur un refus d'authentification${ou ? ` (appel : ${ou})` : ''}${code ? ` [${code}${raison ? `/${raison}` : ''}]` : ''} — ${motif || 'sans motif'}`);
  try { sessionStorage.setItem(CLE_MOTIF, motif || 'Votre session a expiré — merci de vous reconnecter.'); } catch (e) {}
  try {
    // L'appel refusé, sans son adresse complète ni son jeton : la méthode et
    // le chemin suffisent à retrouver la ligne dans le journal du serveur.
    const appel = ou ? String(ou).replace(BASE, '').replace(/([?&]token=)[^&]*/i, '$1…') : null;
    sessionStorage.setItem(CLE_DETAIL, JSON.stringify({ appel, code, raison, quand: new Date().toISOString() }));
  } catch (e) {}
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organisation');
  } catch (e) {}
  window.location.reload();
}

/**
 * LA SESSION GLISSE — et c'est ici qu'on la fait glisser.
 *
 * Vécu 22.09 : « le token expire sans qu'on le sache, et plus vite qu'avant ».
 * Le jeton valait 24 h depuis le premier jour, mais en ABSOLU, depuis la
 * connexion : quelqu'un qui travaille tous les jours était éjecté une fois par
 * jour, toujours au milieu de quelque chose.
 *
 * Le serveur renouvelle désormais le jeton passé la moitié de sa vie et le
 * pose en en-tête de réponse. On le range ICI, dans l'enveloppe qui entoure
 * déjà `fetch` : les 642 appels du front en profitent sans en connaître un
 * mot. Le mettre dans le corps des réponses aurait obligé chacun d'eux à le
 * chercher — et le premier oubli aurait fait une session qui ne glisse
 * qu'à moitié.
 */
function rangerJetonRenouvele(reponse) {
  try {
    const neuf = reponse.headers && reponse.headers.get(ENTETE_JETON);
    const courant = localStorage.getItem('token');
    // On ne remplace que si une session existe : un jeton posé sur la réponse
    // d'un appel fait pendant une déconnexion en cours en ressusciterait une.
    if (!neuf || !courant || dejaTraite) return;
    if (!jetonPlusRecent(neuf, courant)) {
      if (neuf !== courant) console.warn('🧊 jeton de session plus ancien que le nôtre sur une réponse — ignoré (réponse gardée par le navigateur)');
      return;
    }
    localStorage.setItem('token', neuf);
  } catch (e) { /* stockage indisponible : l'ancien jeton reste, rien ne casse */ }
}

/** Le contenu d'un jeton, lu SANS vérifier sa signature — pour afficher ou
 *  comparer, jamais pour autoriser. null = illisible. */
function chargeDuJeton(jeton) {
  try {
    const charge = String(jeton || '').split('.')[1];
    if (!charge) return null;
    return JSON.parse(decodeURIComponent(escape(atob(charge.replace(/-/g, '+').replace(/_/g, '/')))));
  } catch (e) { return null; }
}

/**
 * UN JETON NE REMPLACE QUE PLUS VIEUX QUE LUI.
 *
 * Vécu 27.09 : « quand j'ouvre le candidat blacklisté, ça me déconnecte » —
 * sur UNE fiche, juste après une reconnexion. Samedi, le serveur avait
 * renouvelé le jeton sur les trois appels de cette fiche, et le navigateur
 * avait rangé ces réponses dans son cache, en-tête compris. Dimanche, fiche
 * rouverte : le serveur répond « inchangé » (304), le navigateur rend la
 * réponse de samedi — avec le jeton de samedi, mort depuis le matin — et on le
 * rangeait ici par-dessus le jeton tout neuf de la connexion. L'appel suivant
 * partait avec lui : déconnexion.
 *
 * Le serveur ne laisse plus ces réponses dans le cache ; ici, on ne fait plus
 * confiance à l'en-tête sans le DATER : il ne remplace le nôtre que s'il a été
 * signé après (`iat`). Un jeton qu'on ne sait pas dater ne remplace rien.
 */
export function jetonPlusRecent(neuf, courant) {
  if (!neuf || neuf === courant) return false;
  const tNeuf = Number(chargeDuJeton(neuf)?.iat);
  if (!Number.isFinite(tNeuf)) return false;
  const tCourant = Number(chargeDuJeton(courant)?.iat);
  if (!Number.isFinite(tCourant)) return true;
  return tNeuf > tCourant;
}

/**
 * « Bearer null » N'EST PAS UN JETON.
 *
 * Vécu 25.09, dans le journal serveur : `jeton refusé (forme) : jwt malformed`
 * sur `/auth/me`, puis sur l'appel suivant, puis le suivant. Personne n'avait
 * falsifié quoi que ce soit : une session venait d'être purgée, et les appels
 * déjà en route ont continué de partir avec `Bearer ${localStorage.getItem(
 * 'token')}` — donc la chaîne « Bearer null ». Le serveur ne peut que refuser,
 * et le journal se remplit de conséquences qui cachent la cause.
 *
 * Ici, l'en-tête est RETIRÉ plutôt qu'envoyé vide : le serveur répond alors
 * « pas d'authentification » (ce qui est vrai) au lieu de « jeton illisible »
 * (ce qui est faux et fait chercher une falsification). Un seul endroit, les
 * 642 appels du front en profitent.
 */
const JETON_ABSENT = /^Bearer\s*(null|undefined|)$/i;
function sansJetonVide(entree, options) {
  try {
    const h = options && options.headers;
    if (!h) return options;
    const lire = (k) => (typeof h.get === 'function' ? h.get(k) : (h[k] ?? h[k.toLowerCase()] ?? h[k.toUpperCase()]));
    const val = lire('Authorization');
    if (!val || !JETON_ABSENT.test(String(val).trim())) return options;
    if (typeof h.delete === 'function') { h.delete('Authorization'); return options; }
    const copie = { ...h };
    for (const k of Object.keys(copie)) if (k.toLowerCase() === 'authorization') delete copie[k];
    return { ...options, headers: copie };
  } catch (e) { return options; }
}

/** Le jeton porté par l'en-tête Authorization d'un appel, sans le « Bearer ». */
function jetonEnvoye(options) {
  try {
    const h = options && options.headers;
    if (!h) return null;
    const val = typeof h.get === 'function' ? h.get('Authorization')
      : (h.Authorization ?? h.authorization ?? h.AUTHORIZATION ?? null);
    const m = val ? String(val).match(/^Bearer\s+(\S+)$/i) : null;
    return m ? m[1] : null;
  } catch (e) { return null; }
}
/** Les mêmes options, avec CE jeton en en-tête. */
function avecJeton(options, jeton) {
  const h = options && options.headers;
  if (h && typeof h.set === 'function') { h.set('Authorization', `Bearer ${jeton}`); return options; }
  const copie = { ...(h || {}) };
  for (const k of Object.keys(copie)) if (k.toLowerCase() === 'authorization') delete copie[k];
  copie.Authorization = `Bearer ${jeton}`;
  return { ...(options || {}), headers: copie };
}

export function installerIntercepteur401() {
  const fetchOriginal = window.fetch.bind(window);
  window.fetch = async (entree, options) => {
    const reponse = await fetchOriginal(entree, sansJetonVide(entree, options));
    try {
      const url = typeof entree === 'string' ? entree : (entree && entree.url) || '';
      if (url.startsWith(BASE)) rangerJetonRenouvele(reponse);
      if (reponse.status !== 401 || !url.startsWith(BASE)) return reponse;
      if (!localStorage.getItem('token')) return reponse;   // échec de connexion : au formulaire
      if (dejaTraite) return new Promise(() => {});          // rechargement en cours : on absorbe
      const corps = await reponse.clone().json().catch(() => null);
      // Un 401 qui n'est pas un refus d'authentification suit son cours :
      // l'écran l'affiche comme n'importe quelle erreur, et la session reste.
      if (!estRefusAuth(corps)) {
        console.warn(`⚠️ 401 qui n'est PAS une fin de session (${url}) — la session est conservée`, corps || '');
        return reponse;
      }
      // LE JETON REFUSÉ EST-IL ENCORE CELUI DE LA SESSION ? (27.09)
      // Depuis la session glissante, le serveur REMPLACE le jeton en cours de
      // route ; un écran qui avait gardé l'ancien (capturé au rendu, dans une
      // minuterie) finit par se faire refuser — alors que la session, elle, est
      // vivante et son jeton frais dans le stockage. Fermer la session sur ce
      // refus-là, c'est éjecter quelqu'un de connecté. On REJOUE l'appel avec
      // le jeton du moment ; si le serveur le refuse aussi, alors oui, la
      // session est finie.
      const envoye = jetonEnvoye(options);
      const courant = localStorage.getItem('token');
      if (envoye && courant && envoye !== courant) {
        console.warn(`🔁 ${url} refusé avec un jeton périmé (${corps.code}) — la session a été renouvelée entre-temps, rejoué avec le jeton courant`);
        const rejoue = await fetchOriginal(entree, avecJeton(options, courant));
        if (rejoue.status !== 401) return rejoue;
        const corps2 = await rejoue.clone().json().catch(() => null);
        if (!estRefusAuth(corps2)) return rejoue;
        fermerSession(corps2.error, url, corps2);
        return new Promise(() => {});
      }
      fermerSession(corps.error, url, corps);
      return new Promise(() => {});
    } catch (e) {
      // L'intercepteur n'introduit jamais de panne : en cas de doute, la
      // réponse originale suit son cours normal.
      return reponse;
    }
  };
}

/**
 * Quand la session se ferme, en millisecondes epoch — lu dans le jeton lui-même
 * (`exp`), sans vérifier sa signature : c'est de l'AFFICHAGE, pas un contrôle.
 * Le contrôle est au serveur, et il y restera ; un front qui « valide » un
 * jeton se raconte une histoire, puisqu'il détient déjà le jeton.
 *
 * null = pas de session, ou un jeton qu'on ne sait pas lire — dans le doute on
 * ne promet rien, et l'application se comporte comme avant.
 */
export function finDeSession() {
  try {
    const jeton = localStorage.getItem('token');
    if (!jeton) return null;
    const json = chargeDuJeton(jeton);
    if (!json) return null;
    const exp = Number(json.exp);
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch (e) { return null; }
}

/** Le détail de la dernière fermeture ({ appel, code, raison, quand }), consommé à la lecture. */
export function detailSessionExpiree() {
  try {
    const d = sessionStorage.getItem(CLE_DETAIL);
    if (d) sessionStorage.removeItem(CLE_DETAIL);
    return d ? JSON.parse(d) : null;
  } catch (e) { return null; }
}

/** Motif à afficher sur la page de connexion, consommé à la lecture. */
export function motifSessionExpiree() {
  try {
    const m = sessionStorage.getItem(CLE_MOTIF);
    if (m) sessionStorage.removeItem(CLE_MOTIF);
    return m;
  } catch (e) { return null; }
}
